import { HourlyWorkType } from '@app/models/ebo/digital-logs/hourly-work-type.enum';
import { isString } from '@summit/core';
import {
  DaysOfWeek,
  DaysOfWeekToString,
  MileageFormatter,
  MoneyFormatter,
  NumberFormatter,
  PercentageFormatter,
  RateFormatter,
  SummitFormattedText
} from '@summit/models';
import { EboObjectTO } from '../../base-classes/ebo-object-to';
import { EmployeeWageRuleLogic } from '../../employee/wage/employee-wage-rule-logic.enum';
import { AdjustmentType } from '../../payroll/employee-adj/adjustment-type.enum';
import { PayUnit } from '../../payroll/pay-unit.enum';
import { PayrollSource } from '../../payroll/payroll-source.enum';
import { Hub } from '../../places/hub/hub';
import { Route } from '../../route/route';
import { RouteGroup } from '../../route/route-group/route-group';
import { DigitalLogType, DigitalLogTypeUserFriendlyMapping } from '../digital-log-type.enum';

export class EWageRule extends EboObjectTO {
  ruleOrder: number;
  logic: EmployeeWageRuleLogic;
  //Filter
  days: DaysOfWeek;
  driverOffDays: boolean;
  logType: DigitalLogType;
  routeId: string;
  route: Route;
  routeGroupId: string;
  routeGroup: RouteGroup;
  origHubId: number;
  origHub: Hub;
  destHubId: number;
  destHub: Hub;
  maxMiles: number;
  workType: HourlyWorkType;
  //Pay Category
  adjustmentType: AdjustmentType;
  payrollSource: PayrollSource;
  //Pay Amount
  incremental: boolean;
  units: PayUnit;
  mileageRateSolo: number; //applicable based on units
  mileageRateTeam: number; //applicable based on units
  spotRate: number; //applicable based on units
  spotAmountRate: number;
  dailyPay: number;
  comment: string;
  blockDayGuarantee: boolean;
  //Overages
  mileageThreshold: number;
  mileageThresholdRate: number;
  mileageThresholdPayMin: number;
  stopThreshold: number;
  stopThresholdAmount: number;
  spotThreshold: number;
  spotThresholdAmount: number;

  constructor(jsonData: Partial<EWageRule>) {
    super(jsonData);
    //console.log('EWageRule', jsonData);
    if (jsonData) {
      if (jsonData.route) this.route = new Route(jsonData.route);
      if (jsonData.routeGroup) this.routeGroup = new RouteGroup(jsonData.routeGroup);
      if (jsonData.origHub) this.origHub = new Hub(jsonData.origHub);
      if (jsonData.destHub) this.destHub = new Hub(jsonData.destHub);
    }
    this.days ??= DaysOfWeek.AllDays;
    if (isString(this.units)) this.units = parseInt(this.units);
    //console.log('EmployeeWageRule', this);
    this.logic ??= EmployeeWageRuleLogic.Day;
    this.blockDayGuarantee ??= false;
    this.incremental ??= false;
    this.logType ??= DigitalLogType.Any;
    this.driverOffDays ??= false;
    if (isString(this.logType)) this.logType = parseInt(this.logType);
  }

  get dayString(): SummitFormattedText {
    const response = new SummitFormattedText();
    response.AddLine(DaysOfWeekToString(this.days));
    if (this.driverOffDays) response.AddLine('Off Days Only');
    return response;
  }

  get paySummary(): SummitFormattedText {
    const response = new SummitFormattedText();
    if (this.logType === DigitalLogType.Daily) {
      if (this.workType !== undefined && this.workType !== null) {
        response.AddKeyValues(`Daily Log`, HourlyWorkType[this.workType]);
      } else response.AddLine(`Daily Log`, ['flex', 'grow', 'justify-center', 'text-bold']);
    } else {
      if (this.logType !== DigitalLogType.Any)
        response.AddLine(DigitalLogTypeUserFriendlyMapping[this.logType], [
          'flex',
          'grow',
          'justify-center',
          'text-bold'
        ]);
    }
    if (this.route) response.AddKeyValues(`Route`, this.route.name);
    if (this.routeGroup) response.AddKeyValues(`Route Group`, this.routeGroup.name);
    const prefix = this.logic == EmployeeWageRuleLogic.Day ? 'Per Day' : `Leg ${this.origHubId}=>${this.destHubId}`;
    if (this.incremental) response.AddLine('Incremental');
    switch (this.units) {
      case PayUnit.Dollars:
        if (this.dailyPay || !this.incremental) response.AddKeyValues(prefix, MoneyFormatter(this.dailyPay));
        break;
      case PayUnit.PercentageGrossNetFuel:
        if (this.mileageRateSolo)
          response.AddKeyValues('Solo % Gross Net', PercentageFormatter(this.mileageRateSolo / 100));
        if (this.mileageRateTeam)
          response.AddKeyValues('Team % Gross Net', PercentageFormatter(this.mileageRateTeam / 100));
        if (this.spotRate) response.AddKeyValues('Spot % Gross Net', PercentageFormatter(this.spotRate / 100));
        break;
      case PayUnit.PercentageGross:
        if (this.mileageRateSolo)
          response.AddKeyValues('Solo % Gross', PercentageFormatter(this.mileageRateSolo / 100));
        if (this.mileageRateTeam)
          response.AddKeyValues('Team % Gross', PercentageFormatter(this.mileageRateTeam / 100));
        if (this.spotRate) response.AddKeyValues('Spot % Gross', PercentageFormatter(this.spotRate / 100));
        break;
      case PayUnit.PercentageGrossNetFuelNoDH:
        if (this.mileageRateSolo)
          response.AddKeyValues('Solo % Gross Net (No DH)', PercentageFormatter(this.mileageRateSolo / 100));
        if (this.mileageRateTeam)
          response.AddKeyValues('Team % Gross Net (No DH)', PercentageFormatter(this.mileageRateTeam / 100));
        if (this.spotRate) response.AddKeyValues('Spot % Gross Net', PercentageFormatter(this.spotRate / 100));
        break;
      case PayUnit.PercentageGrossNoDH:
        if (this.mileageRateSolo)
          response.AddKeyValues('Solo % Gross (No DH)', PercentageFormatter(this.mileageRateSolo / 100));
        if (this.mileageRateTeam)
          response.AddKeyValues('Team % Gross (No DH)', PercentageFormatter(this.mileageRateTeam / 100));
        if (this.spotRate) response.AddKeyValues('Spot % Gross', PercentageFormatter(this.spotRate / 100));
        break;
      case PayUnit.MileageRate:
        response.AddLine(prefix);
        if (this.mileageRateSolo) response.AddKeyValues(`Solo`, RateFormatter(this.mileageRateSolo));
        if (this.mileageRateTeam) response.AddKeyValues(`Team`, RateFormatter(this.mileageRateTeam));
        break;
      default:
        break;
    }
    if (this.spotAmountRate) response.AddKeyValues('Spot Amount %', PercentageFormatter(this.spotAmountRate / 100));
    if (this.maxMiles) response.AddKeyValues(`Max Miles`, MileageFormatter(this.maxMiles));
    if (this.mileageThreshold) {
      let lclDescription = `${RateFormatter(this.mileageThresholdRate)} over ${MileageFormatter(
        this.mileageThreshold
      )} Miles`;
      if (this.mileageThresholdPayMin) lclDescription += ` (${MoneyFormatter(this.mileageThresholdPayMin)} Min)`;
      response.AddLine(lclDescription);
    }
    if (this.stopThreshold)
      response.AddLine(`${MoneyFormatter(this.stopThresholdAmount)} over ${NumberFormatter(this.stopThreshold)} Stops`);
    if (this.spotThresholdAmount)
      response.AddLine(`${MoneyFormatter(this.spotThresholdAmount)} over ${NumberFormatter(this.spotThreshold)} Spots`);
    if (this.blockDayGuarantee) response.AddLine('Day Guarantee Blocked');
    return response;
  }
}
